import { Banner } from "@shopify/polaris";

import type { BarcodeLabelSheetResult } from "../utils/barcodeLabels";

import { t } from "../lib/i18n";

/** 印刷結果のうち画面へ報告する部分（HTML 本体は保持しない）。 */
export type BarcodeLabelReport = Omit<BarcodeLabelSheetResult, "html">;

interface Props {
  result: BarcodeLabelReport | null;
  onDismiss: () => void;
}

/**
 * バーコードラベル印刷の結果報告。発注書と商品一覧の両方から使う。
 *
 * 🔴 出た枚数だけでなく「出さなかったもの」と理由を必ず出す。
 * 黙って減らすと「押したのに全部出ない」が原因不明のまま残り、
 * 貼り忘れた棚がスキャンできないところまで気付けない。
 */
export function BarcodeLabelResultBanner({ result, onDismiss }: Props) {
  if (result === null) return null;

  const isEmpty = result.labelCount === 0;

  return (
    <Banner
      tone={isEmpty ? "warning" : "info"}
      title={
        isEmpty
          ? t("印刷できるバーコードラベルがありませんでした")
          : t("バーコードラベル {n}枚を印刷用に開きました", { n: result.labelCount })
      }
      onDismiss={onDismiss}
    >
      {result.skippedNoBarcode > 0 && (
        <p>
          {t("{n}件の商品はバーコード未設定のため、ラベルを出していません。", {
            n: result.skippedNoBarcode,
          })}
          {t("Shopify の商品情報にバーコード（JAN）を設定してから印刷し直してください。")}
        </p>
      )}
      {result.textOnlyCount > 0 && (
        <p>
          {t("{n}件の商品はJAN（13桁・8桁）として読めないコードのため、", {
            n: result.textOnlyCount,
          })}
          {t("バーの画像は出さずコード文字列だけを印刷しています（スキャンはできません）。")}
        </p>
      )}
    </Banner>
  );
}
